import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useToggle, useList } from 'react-use';
import classNames from 'classnames';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import { toast } from 'react-toastify';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import lodash from 'lodash';

import './style.scss';
import TopicTypes from './TopicTypes';
import { CardGrid, CardChart } from '../../../../components/chart';
import Loading from '../../../../components/loading';
import { LIST_VIEW, LINE_CHART_VIEW, COLUMN_CHART_VIEW } from '../../../../components/chart/card-chart';
import { GRID, CIRCLE } from '../../../../components/chart/card-grid';
import {
    getTopStatuses,
    getTopicCell,
    removeTopicCell,
    addTopicCell,
    deleteTopic,
    getDetailTopic,
    copyTopic,
    updateTopic,
    getCategoryTopics,
    updateTopicCell,
    getConfigCells,
    ACTIONS,
} from '../../../../actions/user-topic';
import TargetTopicBox from './TargetTopicBox';
import TopicDelete from '../../my-topic/modal/TopicDelete';
import ConditionModal from './ConditionModal';
import SettingModal from './SettingModal';
import { getMasterTeams, getMasterYears } from "../../../../actions/metadata";
import SwapItemModal from '../../leader-board/SwapItemModal';
import moment from 'moment';

const TABLE = 'normal';
const GRAPH = 'graph';
const HOT_ZONE = 'hotzone';
const BATTED_BALL = 'bbd';

const modalActions = {
    setting: 'setting',
    collapse: 'collapse',
    swapField: 'swapField',
};

function TeamUserTopic() {
    const dispatch = useDispatch();
    const { id, topicId } = useParams();
    const history = useHistory();
    const { url } = useRouteMatch();
    const typeTopic = useSelector((state) => state.userTopic.typeTopic, shallowEqual);
    const currentTitleTopic = useSelector((state) => state.userTopic.currentTitleTopic, shallowEqual);

    const [on, toggle] = useToggle(false);
    const [topicDetail, setTopicDetail] = useState(null);
    const [listStatus, setListStatus] = useState([]);
    const [listTopic, listTopicHelper] = useList([]);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [confirmDeleteModal, toggleModal] = useToggle(false);
    const [loading, setLoading] = useState(false);
    const [invalidTopic, setInvalidTopic] = useState(false);
    const [deliveriedAt, setDeliveriedAt] = useState('');
    const [updatedDateText, setUpdatedDateText] = useState('');
    const [actionModal, setActionModal] = useState('');
    const [currentCell, setCurrentCell] = useState({});
    const [config, setConfig] = useState({
        filters: [],
        columns: [],
    });

    // fetch metadata
    useEffect(() => {
        dispatch(getMasterYears());
        dispatch(getMasterTeams());
    }, []);

    useEffect(() => {
        if (!topicId) return;
        fetchDetailTopic();
    }, [topicId]);

    useEffect(() => {
        fetchTopStatus();
    }, []);

    useEffect(() => {
        if (!topicDetail || listStatus.length === 0) return;

        updateSelectedStatus();
    }, [listStatus, topicDetail]);

    const fetchDetailTopic = useCallback(async () => {
        try {
            listTopicHelper.clear();
            setInvalidTopic(false);
            setLoading(true);
            dispatch({
                type: ACTIONS.SET_TITLE_TOPIC,
                title: '',
            });

            const { cells, topic, updatedDateText } = await dispatch(getDetailTopic(topicId, id));
            setLoading(false);
            dispatch({
                type: ACTIONS.SET_TITLE_TOPIC,
                title: topic.title,
            });
            setDeliveriedAt(topic.workspace ? topic.workspace.deliveried_at : '');
            listTopicHelper.set(cells);
            setTopicDetail(topic);
            setUpdatedDateText(updatedDateText);
        } catch (error) {
            setLoading(false);
            setInvalidTopic(true);
        }
    }, [topicId]);

    const fetchTopStatus = useCallback(async () => {
        try {
            const data = await dispatch(getTopStatuses());
            if (!data) {
                return;
            }
            setListStatus(data);
        } catch (error) {
            toast.error('Server error')
        }
    }, []);

    const updateSelectedStatus = useCallback(() => {
        const findItem = topicDetail.topic_status || listStatus[0];
        setSelectedStatus(findItem);
    }, [listStatus, topicDetail]);

    const onUpdateTopic = useCallback(async (status) => {
        try {
            await dispatch(updateTopic({
                topicId,
                topicStatusId: status.id,
            }));
        } catch (error) {
            toast.error('Server error')
        }
    }, [topicId]);

    const onDropped = useCallback(async (item) => {
        try {
            const addCellResponse = await dispatch(addTopicCell({
                topicId,
                topicsSourceId: item.id,
                order: listTopic.length + 1,
            }));
            let data = await dispatch(getTopicCell(topicId, addCellResponse.id));
            const topic = item.typeItem === 'normal' ? {
                ...item,
                title: data.title || addCellResponse.title,
                columns: data.noraml.columns,
                data: data.noraml.data,
                filters: addCellResponse.filters,
                id: addCellResponse.id,
                updated_date_text: data.updated_date_text || addCellResponse.updated_date_text || '', // ←追加
            } : {
                ...item,
                ...data,
                id: addCellResponse.id,
                updated_date_text: data.updated_date_text || addCellResponse.updated_date_text || '', // ←追加
            };
            listTopicHelper.push(topic);
        } catch (error) {
            console.log(error);
        }

    }, [selectedStatus, topicId, listTopic]);

    const onDeleteTopic = useCallback(async () => {
        try {
            setLoading(true);
            await dispatch(deleteTopic(topicId));
            setLoading(false);
            toggleModal();
            dispatch(getCategoryTopics(typeTopic, id));
            const link = url.split('/topic')[0];
            history.push(link);
        } catch (error) {
            setLoading(false);
        }
    }, [topicId, typeTopic, id]);

    const onConfirmDeleteTopic = useCallback(() => {
        toggleModal();
    }, []);

    const onCopyTopic = useCallback(async () => {
        try {
            setLoading(true);
            await dispatch(copyTopic(topicId));
            dispatch(getCategoryTopics(typeTopic, id));
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    }, [topicId, typeTopic, id]);

    const onActionCell = useCallback(async (action, item, index) => {
        setCurrentCell(item);
        if (action.value === 'remove') {
            try {
                await dispatch(removeTopicCell({
                    topicId: topicId,
                    cellId: item.id,
                }));
                listTopicHelper.removeAt(index);
            } catch (error) {
                console.log(error);
            }
            return;
        }

        // get a cell configs
        const response = await dispatch(getConfigCells({
            topicId: topicId,
            cellId: item.id,
        }));
        setConfig(response);

        if (action.value === modalActions.collapse) {
            setActionModal(modalActions.collapse);
            return;
        }

        if (action.value === modalActions.setting) {
            setActionModal(modalActions.setting);
            return;
        }

        if (action.value === modalActions.swapField) {
            const intersection = lodash.intersectionWith(response.columns, item.columns, lodash.isEqual);
            if (intersection) {
                setCurrentCell({
                    ...item,
                    rawColumns: intersection,
                })
            } else {
                setConfig({
                    ...config,
                    columns: lodash.differenceWith(response.columns, item.columns, lodash.isEqual)
                })

            }
            setActionModal(modalActions.swapField);
            return;
        }
    }, [topicId, config]);

    const onSaveCondition = useCallback(async (list) => {
        await dispatch(updateTopicCell({
            topicId: topicId,
            cellId: currentCell.id,
            filters: list.map((item) => {
                return {
                    column: item.column,
                    value: item.selected,
                }
            })
        }));
        fetchDetailTopic();
        onCloseModal();
    }, [currentCell, topicId]);

    const onSaveSetting = useCallback(async (graphs) => {
        await dispatch(updateTopicCell({
            topicId: topicId,
            cellId: currentCell.id,
            graphs,
        }));
        fetchDetailTopic();
        onCloseModal();
    }, [currentCell, topicId]);

    const onSaveSwapItem = useCallback(async (list) => {
        await dispatch(updateTopicCell({
            topicId: topicId,
            cellId: currentCell.id,
            columns: lodash.map(list, 'column')
        }));
        fetchDetailTopic();
        onCloseModal();
    }, [currentCell, topicId]);

    const onCloseModal = useCallback(() => {
        setActionModal('');
    }, []);

    const onUpdateNoteCell = useCallback(async (cell, note, index) => {
        await dispatch(updateTopicCell({
            topicId: topicId,
            cellId: cell.id,
            memo: note,
        }));

        listTopicHelper.updateAt(index, {
            ...cell,
            memo: note
        })
    }, [topicId]);

    const onUpdateTitleCell = useCallback(async (cell, title, index) => {
        await dispatch(updateTopicCell({
            topicId: topicId,
            cellId: cell.id,
            title,
        }));
        listTopicHelper.updateAt(index, {
            ...cell,
            title
        })
    }, [topicId]);

    const onToggleWidth = useCallback(async (index, cell) => {
        listTopicHelper.updateAt(index, {
            ...cell,
            toggleWidth: !cell.toggleWidth,
        });
    }, []);

    const renderTopic = useCallback((item, index) => {
        if (item.typeItem === TABLE) {
            return <CardChart
                data={{
                    headers: item.columns,
                    values: item.data,
                }}
                title={item.title}
                description={item.memo}
                type={LIST_VIEW}
                updated_date_text={item.updated_date_text ? item.updated_date_text : ''}
                toggleWidth={item.toggleWidth}
                onChangeAction={(action) => onActionCell(action, item, index)}
                onUpdateNote={(note) => onUpdateNoteCell(item, note, index)}
                onUpdateTitle={(title) => onUpdateTitleCell(item, title, index)}
                onToggleWidth={() => onToggleWidth(index, item)}
            />;
        }

        if (item.typeItem === GRAPH) {
            return <CardChart
                data={{
                    headers: [],
                    values: item.graph.data,
                    options: item.graph,
                }}
                title={item.title}
                toggleWidth={item.toggleWidth}
                description={item.memo}
                type={item.chart === 'line' ? LINE_CHART_VIEW : COLUMN_CHART_VIEW}
                updated_date_text={item.updated_date_text ? item.updated_date_text : ''}
                onChangeAction={(action) => onActionCell(action, item, index)}
                onUpdateNote={(note) => onUpdateNoteCell(item, note, index)}
                onUpdateTitle={(title) => onUpdateTitleCell(item, title, index)}
                onToggleWidth={() => onToggleWidth(index, item)}
            />;
        }

        if (item.typeItem === HOT_ZONE) {
            return <CardGrid
                title={item.title}
                description={item.memo}
                data={item.hotzone}
                type={GRID}
                onChangeAction={(action) => onActionCell(action, item, index)}
                onUpdateNote={(note) => onUpdateNoteCell(item, note, index)}
                onUpdateTitle={(title) => onUpdateTitleCell(item, title, index)}
            />;
        }

        if (item.typeItem === BATTED_BALL) {
            return <CardGrid
                title={item.title}
                description={item.memo}
                data={item.bbd}
                type={CIRCLE}
                onChangeAction={(action) => onActionCell(action, item, index)}
                onUpdateNote={(note) => onUpdateNoteCell(item, note, index)}
                onUpdateTitle={(title) => onUpdateTitleCell(item, title, index)}
            />;
        }
        return null;
    }, [topicId]);

    return <DndProvider backend={Backend}>
        <div className="team-user-topic">
            {invalidTopic ? <div style={{ textAlign: 'center', paddingTop: 15 }}>
                トピックが無効です
            </div> : <>
                <div className="block-control">
                    <div className="form-control" style={{ width: 150 }} onClick={toggle}>
                        <div id='option-selected'>
                            <div className={`option-select option-${selectedStatus ? selectedStatus.id : 1}`}>
                                {selectedStatus ? selectedStatus.name : ''}
                            </div>
                        </div>
                        {on && <div>
                            {listStatus.map(item => <div key={item.id}
                                className={classNames('option', `option-${item.id}`)}
                                onClick={() => {
                                    setSelectedStatus(item);
                                    onUpdateTopic(item);
                                }}>
                                {item.name}
                            </div>)}
                        </div>}
                    </div>
                    {topicDetail && typeTopic === 'game' && topicDetail.category_name !== 'game' ? null : <div className="block-button">
                        {!deliveriedAt && <button type="button" className="btn-delete" onClick={onConfirmDeleteTopic}>
                            Topicsを削除
                        </button>}
                        <button type="button" className="btn-copy" onClick={onCopyTopic}>
                            Topicsをコピー
                        </button>
                    </div>}
                </div>
                <div className={'team-user-list-topic'}>
                    {listTopic.map((item, index) => <div key={item.id}
                        style={{ width: item.toggleWidth ? '100%' : '50%', padding: '8px' }}>
                        {renderTopic(item, index)}
                    </div>)}
                    {listTopic.length < 8 && <TargetTopicBox onDropped={onDropped} />}
                </div>
                <TopicTypes />
                {confirmDeleteModal && <TopicDelete
                    item={{ title: currentTitleTopic }}
                    onClose={toggleModal}
                    onSave={onDeleteTopic} />}
            </>}
            {actionModal === modalActions.collapse && <ConditionModal
                selected={currentCell.filters}
                configs={config.filters}
                type={currentCell.typeItem}
                onSave={onSaveCondition}
                onClose={onCloseModal}
            />}
            {actionModal === modalActions.setting && <SettingModal
                selected={currentCell.graphs}
                configs={config.graphs}
                onSave={onSaveSetting}
                onClose={onCloseModal}
            />}
            {actionModal === modalActions.swapField && <SwapItemModal
                leftColumns={config.columns}
                rightColumns={currentCell.rawColumns || []}
                onConfirm={onSaveSwapItem}
                onCloseModal={onCloseModal}
            />}
            <Loading show={loading} />
        </div>
    </DndProvider>

}

export default TeamUserTopic;
